/**
 * Cart interactions: add, change quantity, remove, and the mini-cart drawer.
 *
 * All handlers are delegated from `document`, so they survive the product grid
 * being replaced by a filter change and work inside the drawer, which is
 * re-rendered on every update.
 */

import { $, $$, delegate, money } from '@/lib/dom'
import { api, ApiError, withBase, type CartStateDTO } from '@/lib/api'
import { app } from '@/store/app'
import { toast } from './toast'
import { openDrawer } from './drawer'

/** Push a server cart response into the store and repaint the drawer. */
function commit(state: CartStateDTO): void {
  app.set({ cartCount: state.count, cartSubtotal: state.subtotal })
  renderDrawer(state)
}

/** Header badge + free-shipping meter follow the store, not the API call. */
function renderBadges(count: number): void {
  $$('[data-cart-count]').forEach((badge) => {
    badge.textContent = String(count)
    badge.hidden = count === 0

    // Re-trigger the bump animation by removing and re-adding the class.
    badge.classList.remove('is-bumped')
    void badge.offsetWidth
    badge.classList.add('is-bumped')
  })
}

function renderDrawer(state: CartStateDTO): void {
  const body = $('[data-cart-lines]')
  if (!body) return

  if (state.lines.length === 0) {
    body.innerHTML = `
      <div class="empty">
        <span class="empty__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <path d="M6 6h15l-1.5 9h-12z"/><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M6 6 5 2H2"/>
          </svg>
        </span>
        <p class="text-muted">Your bag is empty.</p>
        <a class="btn btn--outline btn--sm" href="${withBase('/shop.php')}">Start shopping</a>
      </div>`
  } else {
    body.innerHTML = state.lines
      .map(
        (line) => `
      <div class="line" data-line="${line.id}">
        <span class="line__art" style="background:linear-gradient(135deg, ${escapeAttr(line.gradient)})"></span>
        <div class="line__body">
          <div class="line__top">
            <div>
              <div class="line__brand">${escapeHtml(line.brand)}</div>
              <a class="line__name" href="${withBase('/product.php')}?slug=${encodeURIComponent(line.slug)}">${escapeHtml(line.name)}</a>
            </div>
            <span class="price">${money(line.line_total)}</span>
          </div>
          <div class="line__controls">
            <span class="qty">
              <button type="button" data-qty-step="-1" data-product="${line.id}" aria-label="Decrease quantity">−</button>
              <input type="number" value="${line.quantity}" min="0" data-qty-input data-product="${line.id}" aria-label="Quantity">
              <button type="button" data-qty-step="1" data-product="${line.id}" aria-label="Increase quantity">+</button>
            </span>
            <button type="button" class="line__remove" data-cart-remove="${line.id}">Remove</button>
          </div>
        </div>
      </div>`,
      )
      .join('')
  }

  const subtotal = $('[data-cart-subtotal]')
  if (subtotal) subtotal.textContent = money(state.subtotal)

  const foot = $('[data-cart-foot]')
  if (foot) foot.hidden = state.lines.length === 0

  renderMeter(state)
}

function renderMeter(state: CartStateDTO): void {
  const meter = $('[data-ship-meter]')
  if (!meter) return

  const remaining = state.free_shipping_remaining
  const threshold = state.free_shipping_threshold
  const label = $('[data-ship-label]', meter)
  const fill = $<HTMLElement>('[data-ship-fill]', meter)

  if (label) {
    label.textContent =
      remaining === 0
        ? 'You have unlocked free shipping.'
        : `${money(remaining)} away from free shipping.`
  }

  if (fill) {
    const progress = threshold === 0 ? 100 : ((threshold - remaining) / threshold) * 100
    fill.style.width = `${Math.max(0, Math.min(100, progress))}%`
  }
}

/** Escape helpers — cart lines contain server-supplied product names. */
function escapeHtml(value: string): string {
  const node = document.createElement('span')
  node.textContent = value

  return node.innerHTML
}

function escapeAttr(value: string): string {
  return value.replace(/["'<>]/g, '')
}

export function initCart(): void {
  // Badge always reflects the store.
  app.subscribe((state, previous) => {
    if (state.cartCount !== previous.cartCount) renderBadges(state.cartCount)
  })

  // ---- Add to bag ----------------------------------------------------------
  delegate(document, 'click', '[data-add-to-cart]', (event, button) => {
    event.preventDefault()

    const productId = Number(button.dataset.addToCart)
    if (!Number.isFinite(productId)) return

    const quantityInput = $<HTMLInputElement>('[data-qty-input][data-buy-qty]')
    const quantity = quantityInput ? Math.max(1, Number(quantityInput.value) || 1) : 1

    button.classList.add('is-loading')

    void api
      .addToCart(productId, quantity)
      .then((state) => {
        commit(state)
        toast(state.message, 'success')
        // Opening the drawer is the confirmation — it shows what was added
        // and the running total without leaving the page.
        openDrawer('cart')
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : 'Could not add to bag.', 'error')
      })
      .finally(() => button.classList.remove('is-loading'))
  })

  // ---- Quantity stepper ----------------------------------------------------
  delegate(document, 'click', '[data-qty-step]', (event, button) => {
    event.preventDefault()

    const step = Number(button.dataset.qtyStep)
    const productId = Number(button.dataset.product)
    const input = button.parentElement?.querySelector<HTMLInputElement>('[data-qty-input]')
    if (!input) return

    const next = Math.max(0, (Number(input.value) || 0) + step)
    input.value = String(next)

    // A stepper without a product id is a standalone picker (the PDP buy box),
    // which must not touch the cart until "Add to bag" is pressed.
    if (!Number.isFinite(productId) || productId === 0) return

    void api
      .updateCartQuantity(productId, next)
      .then(commit)
      .catch(() => toast('Could not update quantity.', 'error'))
  })

  // ---- Typed quantity ------------------------------------------------------
  delegate(document, 'change', '[data-qty-input]', (_event, node) => {
    const input = node as HTMLInputElement
    const productId = Number(input.dataset.product)
    if (!Number.isFinite(productId) || productId === 0) return

    const quantity = Math.max(0, Number(input.value) || 0)

    void api
      .updateCartQuantity(productId, quantity)
      .then(commit)
      .catch(() => toast('Could not update quantity.', 'error'))
  })

  // ---- Remove --------------------------------------------------------------
  delegate(document, 'click', '[data-cart-remove]', (event, button) => {
    event.preventDefault()

    const productId = Number(button.dataset.cartRemove)
    const line = button.closest<HTMLElement>('[data-line]')

    // Fade the row out immediately; the request confirms it a moment later.
    line?.style.setProperty('opacity', '0.4')

    void api
      .removeFromCart(productId)
      .then((state) => {
        commit(state)
        toast('Removed from your bag.', 'info')
        // On the full cart page the row is server-rendered, so reload to
        // recalculate totals rather than patching the DOM in two places.
        if (document.body.dataset.page === 'cart') window.location.reload()
      })
      .catch(() => {
        line?.style.removeProperty('opacity')
        toast('Could not remove that item.', 'error')
      })
  })

  renderBadges(app.get().cartCount)
}
